import * as React from 'react';
import { IconButton } from 'omakase';
import { IconClose, IconSearch as IconSearchLegacy } from 'ui/icons';
import { IconCrossLarge, IconSearch } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { InputAdornment } from '@material-ui/core';
import {
  SearchBarOptionOrSectionDivider,
  SearchNavEntityTypes,
  getKeyForSearchBarOption,
  isSelectableOption,
} from 'utils/searchBar';
import { TextField } from 'ui';
import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { core } from '@tock/coretypes';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ScrollLockComponent from '@tock/shared/components/ScrollLockComponent';
import SearchBarDesktopResult from 'components/nav-search/SearchBarDesktopResult';
import _ from 'lodash';
import clsx from 'clsx';

const styles = (theme: Theme) =>
  createStyles({
    searchNavClose: {
      marginRight: theme.spacing(0.5),
    },
    searchNavInputContainer: {
      alignItems: 'center',
      borderRadius: theme.borderRadius[100],
      display: 'flex',
      flex: '1 0 0',
      minHeight: theme.spacing(6),
    },
    resultsContainer: {
      maxHeight: theme.typography.pxToRem(600),
      '& .SearchNav-destinationSearchHeading': {
        borderBottom: 'none',
        padding: theme.spacing(2),
      },
      '& .SearchNav-destinationSearchLink': {
        borderBottom: 'none',
        padding: theme.spacing(2),
      },
    },
    searchIcon: {
      fontSize: theme.typography.pxToRem(20),
    },
    searchNavTitle: {
      alignItems: 'center',
      display: 'flex',
      flex: '1 0 0',
      justifyContent: 'center',
    },
    searchNavDestinationSearchInput: {
      border: '0 !important',
      color: theme.colors.fgSubtle,
      flex: '1 0 calc(100% - 10rem)',
      height: '100%',
      lineHeight: 'normal',
      maxHeight: theme.spacing(8),
      minHeight: theme.spacing(6),
      minWidth: 'calc(100% - 10rem)',
      outline: 'none',
      position: 'relative',
      width: '100%',
      '&:focus': {
        outline: 'none',
      },
      '&[disabled]': {
        opacity: 1,
      },

      '& .MuiInputBase-root': {
        border: '0 !important',

        '&:hover': {
          border: '0 !important',
        },

        '&:focus': {
          border: '0 !important',
        },
      },
    },
    searchNavEmptyState: {
      alignSelf: 'flex-start',
      margin: theme.typography.pxToRem(21),
    },
  });

interface Props extends WithStyles<typeof styles> {
  autocomplete(searchText: string): void;
  className?: string;
  clearSearchResults(): void;
  currentMetroArea?: core.MetroArea;
  onFocus?(event: React.FocusEvent<HTMLElement>): void;
  onSelectEntry(entry: SearchBarOptionOrSectionDivider, idx: number): void;
  placeholder: string;
  preventDefaultOnSelect?: boolean;
  results: SearchBarOptionOrSectionDivider[];
  throttleSuggestions?: boolean;
}

interface State {
  isRequesting: boolean;
  focusIndex: number;
  currentSearch: string;
}

const initialState: State = {
  focusIndex: -1,
  isRequesting: false,
  currentSearch: '',
};

class SearchBarHeaderDesktop extends BaseComponent<Props, State> {
  inputRef = React.createRef<HTMLInputElement>();

  scrollAreaRef = React.createRef<ScrollLockComponent>();

  autocompleteThrottled: (value: string) => void;

  state: State = { ...initialState };

  constructor(props: Props) {
    super(props);
    if (props.throttleSuggestions === false) {
      this.autocompleteThrottled = this.props.autocomplete;
    } else {
      this.autocompleteThrottled = _.debounce(this.props.autocomplete, 200);
    }
  }

  componentDidMount() {
    this.setState((prevState: State) => ({ focusIndex: initialState.focusIndex }));
    this.handleFocus();
  }

  componentWillUnmount() {
    this.props.clearSearchResults();
  }

  initialFocusIndex(resultsToCalculate?: SearchBarOptionOrSectionDivider[]) {
    const { results } = this.props;

    return _.findIndex(resultsToCalculate ?? results, isSelectableOption);
  }

  handleFocus() {
    if (!this.inputRef.current) {
      return;
    }

    this.inputRef.current.focus();
  }

  handleClear(): void {
    this.setState(
      () => ({
        ...initialState,
      }),
      () => {
        this.props.clearSearchResults();
        this.handleFocus();
      }
    );
  }

  /*
   * calculates the next or previous index to be highlighted
   * when navigating up or down (e.g. via arrow keys)
   *
   * @param options - An array of options show in the search bar
   * @param currentIndex - An integer representing the current index
   * @param upDownIndicator - An integer representing direction to move
   *     in the options. Positive indicates increasing indexes. Negative
   *     indicates decreasing indexes. 0 is ignored.
   * @returns an integer representing the new index to switch to
   */
  calculateFocusIndexChange(
    options: SearchBarOptionOrSectionDivider[],
    currentIndex: number,
    upDownIndicator: number
  ): number {
    const modSize = options.length;
    for (let i = 1, l = options.length; i < l; i++) {
      const nextOption = options[(currentIndex + i) % modSize];
      const prevOption = options[(currentIndex + modSize - i) % modSize];
      if (
        upDownIndicator > 0 &&
        nextOption !== 'SEARCH_BAR_SECTION_DIVIDER' &&
        nextOption.category !== SearchNavEntityTypes.HEADING
      ) {
        return (currentIndex + i) % modSize;
      } else if (
        upDownIndicator < 0 &&
        prevOption !== 'SEARCH_BAR_SECTION_DIVIDER' &&
        prevOption.category !== SearchNavEntityTypes.HEADING
      ) {
        return (currentIndex + modSize - i) % modSize;
      }
    }
    return 1;
  }

  handleKeyPress(event: React.KeyboardEvent<HTMLElement>) {
    const { results, onSelectEntry } = this.props;
    if (results) {
      const index = this.state.focusIndex;
      // shift + tab
      if (event.shiftKey && event.keyCode === 9) {
        event.preventDefault();
        this.setFocus(this.calculateFocusIndexChange(results, index, -1));
        return;
      }
      switch (event.key) {
        case 'Tab':
        case 'ArrowDown':
          event.preventDefault();
          this.setFocus(this.calculateFocusIndexChange(results, index, 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setFocus(this.calculateFocusIndexChange(results, index, -1));
          break;
        case 'Enter':
          event.currentTarget.click();
          if (!_.isUndefined(results[index])) {
            onSelectEntry(results[index], index);
          }
          break;
        default:
          this.handleFocus();
          return;
      }
    }
  }

  setFocus(focusIndex: number): void {
    this.setState({
      focusIndex,
    });
  }

  handleSearchChanged(event: React.ChangeEvent<HTMLInputElement>) {
    const { clearSearchResults } = this.props;
    const { value } = event.currentTarget;

    if (value && value.trim().length === 0) {
      clearSearchResults();
      return;
    }

    this.setState(() => ({
      currentSearch: value,
      isRequesting: true,
      focusIndex: initialState.focusIndex,
    }));

    this.autocompleteThrottled(value.trim());
  }

  render() {
    const {
      results,
      classes,
      placeholder,
      onFocus,
      className,
      preventDefaultOnSelect,
      onSelectEntry,
    } = this.props;
    const { currentSearch, focusIndex } = this.state;

    const showClearIcon = !_.isEmpty(currentSearch);

    return (
      <div className={className} data-testid="search-nav-header-desktop">
        <div className={classes.searchNavInputContainer}>
          <TextField
            inputProps={{
              'aria-label': 'Search',
              'data-testid': 'business-search-input',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconMigrationUtil
                    legacyIcon={<IconSearchLegacy className={classes.searchIcon} />}
                    newIcon={<IconSearch className={classes.searchIcon} />}
                  />
                </InputAdornment>
              ),
            }}
            inputRef={this.inputRef}
            className={classes.searchNavDestinationSearchInput}
            placeholder={placeholder}
            onKeyDown={this.handleKeyPress}
            value={currentSearch}
            onChange={this.handleSearchChanged}
            onFocus={onFocus}
          />
          {showClearIcon && (
            <IconButton
              onClick={this.handleClear}
              variant="ghost"
              size="small"
              className={classes.searchNavClose}
              aria-label="Close search"
              data-testid="close-searchnav-button"
            >
              <IconMigrationUtil legacyIcon={<IconClose />} newIcon={<IconCrossLarge />} />
            </IconButton>
          )}
        </div>

        <ScrollLockComponent
          ref={this.scrollAreaRef}
          className={clsx('SearchBar-results', classes.resultsContainer)}
        >
          <ul>
            {results &&
              results.length > 0 &&
              results.map((entry, idx) => {
                if (entry === 'SEARCH_BAR_SECTION_DIVIDER') {
                  // just needed for type compatibility
                  return null;
                }
                return (
                  <SearchBarDesktopResult
                    entry={entry}
                    key={getKeyForSearchBarOption(entry)}
                    selected={idx === focusIndex}
                    onClick={(entry) => onSelectEntry(entry, idx)}
                    onKeyDown={this.handleKeyPress}
                    preventDefaultOnSelect={preventDefaultOnSelect}
                  />
                );
              })}
          </ul>
        </ScrollLockComponent>
      </div>
    );
  }
}

export default withStyles(styles)(SearchBarHeaderDesktop);
